import { useClueWorkOrderByNumberQuery } from '@/buyers/_gen/gql'
import useGqlClient from '@/buyers/hooks/useGqlClient'
import Spinner from '@/gf/components/Spinner'
import TextInput from '@/gf/components/TextInput'
import useDebounce from '@/gf/hooks/useDebounce'
import { useEffect, useRef } from 'react'

export type ClueWorkOrderNumberState = {
  number: string
  id: string | null
  checking: boolean
}

// The "no machine involved" Clue work-order control (GEA-5065): with no
// machine there is no open-WO list to pick from, so the buyer types the WO
// number and we resolve it against Clue (debounced while typing). The server
// re-resolves on submit either way — this input exists so the buyer learns
// about a typo before submitting, and so the parent form can block submit
// until the number is resolved: onChange surfaces {number, id, checking},
// where id stays null until Clue confirms an open WO with that number.
const ClueWorkOrderNumberInput = ({
  value,
  onChange,
}: {
  value: string
  onChange: (state: ClueWorkOrderNumberState) => void
}) => {
  const client = useGqlClient()
  const trimmed = value.trim()
  const debounced = useDebounce(trimmed, 500)

  const { data, loading, error } = useClueWorkOrderByNumberQuery({
    client,
    variables: { number: debounced },
    skip: !debounced,
  })

  // The lookup has settled for the *current* text only once the debounce has
  // caught up and the query is done — a result for stale text must not count.
  const settled = trimmed === debounced && !loading
  const workOrder = trimmed && settled && !error ? (data?.clueWorkOrderByNumber ?? null) : null
  const checking = trimmed !== '' && !settled
  const resolvedId = workOrder ? String(workOrder.id) : null

  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  useEffect(() => {
    onChangeRef.current({ number: value, id: resolvedId, checking })
  }, [value, resolvedId, checking])

  return (
    <div className="space-y-1">
      <div className="relative">
        <TextInput
          value={value}
          setValue={(number) => onChangeRef.current({ number, id: null, checking: true })}
          placeholder="Enter Clue work order number"
        />
        {checking && (
          <div className="absolute inset-y-0 right-3 flex items-center">
            <Spinner className="mx-0" size="xs" color="blue" />
          </div>
        )}
      </div>

      {checking ? (
        <p className="text-sm text-gray-500">Checking work order number…</p>
      ) : workOrder ? (
        <p className="text-sm text-gray-600">
          Found: <span className="font-medium">{workOrder.title || workOrder.number}</span>
          {workOrder.status ? ` · ${workOrder.status}` : ''}
        </p>
      ) : trimmed && settled && error ? (
        <p className="text-sm text-red-600">
          Couldn’t reach Clue to verify the work order number — please try again.
        </p>
      ) : trimmed && settled ? (
        <p className="text-sm text-red-600">No open Clue work order found with this number.</p>
      ) : null}
    </div>
  )
}

export default ClueWorkOrderNumberInput
